import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { AutomationAssignment } from './AutomationAssignment';
import { useAutomationControl } from './useAutomationControl';

export function ConversationAutomation({
  conversationId,
  embedded = false,
}: {
  conversationId: string;
  /** In der Kontextspalte: ohne eigenen Rand und Grund. */
  embedded?: boolean;
}) {
  const { t } = useTranslation('automation');
  const { query, mutation, run } = useAutomationControl(conversationId);
  const problem = useApiProblem(mutation.error ?? query.error);
  const view = query.data;
  const pending = mutation.isPending;
  const error =
    problem?.status === 403
      ? t('forbidden')
      : problem?.status === 409
        ? t(problem.title, { defaultValue: t('stale') })
        : problem?.status === 0
          ? t('unknown')
          : problem
            ? (problem.detail ?? t('failed'))
            : null;
  return (
    <section
      aria-label={t('title')}
      className={
        embedded
          ? '@container'
          : '@container border-line bg-card-alt flex-none border-b px-3 py-2 sm:px-6'
      }
    >
      {!view ? (
        <p className="text-ink-soft text-sm">{query.isPending ? t('loading') : t('unavailable')}</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-ink text-sm font-semibold" role="status">
              {view.control.humanPaused ? t('paused') : t(view.control.mode)}
            </p>
            {view.canOperate && (
              <Button
                type="button"
                variant={view.control.humanPaused ? 'brand' : 'ghost'}
                className="min-h-12"
                disabled={
                  pending || query.isError || (view.control.humanPaused && !view.control.ready)
                }
                onClick={() => run({ action: view.control.humanPaused ? 'resume' : 'takeover' })}
              >
                {pending ? t('pending') : view.control.humanPaused ? t('resume') : t('takeover')}
              </Button>
            )}
          </div>
          {!view.control.ready && (
            <p className="text-ink-soft mt-1 text-sm">
              {t(view.control.readinessReason ?? 'notReady', { defaultValue: t('notReady') })}
            </p>
          )}
          {!view.canOperate && <p className="text-ink-soft mt-1 text-sm">{t('noPermission')}</p>}
          {view.canAssign && (
            <AutomationAssignment
              key={`${conversationId}:${view.control.controlRevision}`}
              view={view}
              pending={pending || query.isError}
              onSave={(ownerInstallationId, mode) =>
                run({ action: 'assign', ownerInstallationId, mode })
              }
            />
          )}
        </>
      )}
      {error && (
        <p role="alert" className="text-sealwax mt-1 text-sm">
          {error}
        </p>
      )}
      {query.isError && (
        <Button
          type="button"
          variant="ghost"
          className="min-h-11"
          onClick={() => void query.refetch()}
        >
          {t('retry')}
        </Button>
      )}
    </section>
  );
}
